import { progressPct } from "@/lib/progress";
import { useEffect, useState, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { SeeItInAction } from "@/components/SeeItInAction";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { HEALTH_COLORS, HEALTH_LABELS, derivePhases } from "@/lib/constants";
import { HEALTH_DOT, actionHealth, riskHealth, decisionHealth, originOf, SEVERITY_BANDS } from "@/lib/health";
import { DotLegend, EditableLine, ExecFiltered, execHeadings, CollapseGroup as ActionGroup } from "@/components/ReportExtras";
import { CHART_DEFS, ReportChart } from "@/components/ReportCharts";
import ReportFlowStrip from "@/components/ReportFlowStrip";
import ReportRoadmap from "@/components/ReportRoadmap";
import { money } from "@/components/BudgetView";
import { toast } from "sonner";
import { FileBarChart, Loader2, Printer, Download, Presentation, Share2, Check, Copy, Mail, Plus, X, ArrowUp, ArrowDown, PanelLeftClose, PanelLeftOpen, ChevronDown, ChevronRight } from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import Markdown from "@/components/Markdown";

const SECTIONS = [
  { key: "exec", label: "Executive summary (AI)" },
  { key: "health", label: "Health overview" },
  { key: "projects", label: "Projects breakdown", portfolioOnly: true },
  { key: "phases", label: "Progress by phase" },
  { key: "milestones", label: "Roadmap & key dates" },
  { key: "minutes", label: "Meeting minutes", projectOnly: true },
  { key: "risks", label: "Risks" },
  { key: "decisions", label: "Decisions", projectOnly: true },
  { key: "approvals", label: "Document approvals", projectOnly: true },
  { key: "actions", label: "Action items" },
  { key: "radar", label: "On the Radar (parked)", projectOnly: true },
  { key: "ioflow", label: "IO Flow pipeline", projectOnly: true },
  { key: "blockers", label: "Blockers" },
  { key: "budget", label: "Budget snapshot" },
];

const sevOf = (s) => (s >= 15 ? { l: "Critical", c: "#EF4444" } : s >= 10 ? { l: "High", c: "#F97316" } : s >= 5 ? { l: "Moderate", c: "#F59E0B" } : { l: "Low", c: "#10B981" });

export default function StatusReport() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [mode, setMode] = useState("project");        // project | portfolio
  const [scope, setScope] = useState("active");        // active | all | custom (portfolio only)
  const [selProjects, setSelProjects] = useState([]);  // custom multi-select project ids
  const [pid, setPid] = useState("");
  const [data, setData] = useState(null);              // single-project data
  const [pdata, setPdata] = useState(null);            // portfolio data
  const [pexec, setPexec] = useState(null);            // portfolio AI summary
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(Object.fromEntries(SECTIONS.map((s) => [s.key, true])));
  const [title, setTitle] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 10));
  const [genExec, setGenExec] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [autoWeekly, setAutoWeekly] = useState(false);

  // ---- report customization state (per scope) ----
  const [secOrder, setSecOrder] = useState(null);
  const [customSections, setCustomSections] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [execHidden, setExecHidden] = useState([]);
  const [selMinutes, setSelMinutes] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [includeDone, setIncludeDone] = useState(true);
  const [panelOpen, setPanelOpen] = useState(true);
  const [charts, setCharts] = useState([]);

  const isPortfolio = mode === "portfolio";
  const multiPick = mode === "project" && selProjects.length > 1;
  const showPortfolio = isPortfolio || multiPick;
  const nonePicked = mode === "project" && selProjects.length === 0;
  const visibleSections = SECTIONS.filter((s) => showPortfolio ? !s.projectOnly : !s.portfolioOnly);
  const scopeKey = showPortfolio ? "portfolio" : (selProjects[0] || "none");

  useEffect(() => {
    const g = (k, d) => { try { const v = JSON.parse(localStorage.getItem(`pf_rp_${k}_${scopeKey}`)); return v === null || v === undefined ? d : v; } catch { return d; } };
    setSecOrder(g("order", null));
    setCustomSections(g("custom", []));
    setOverrides(g("ov", {}));
    setExecHidden(g("exechide", []));
    setSelMinutes(g("minutes", []));
    setIncludeDone(g("incdone", true));
    setCharts(g("charts", []));
  }, [scopeKey]);
  const persist = (k, v) => localStorage.setItem(`pf_rp_${k}_${scopeKey}`, JSON.stringify(v));

  const orderedKeys = useMemo(() => {
    const valid = [...visibleSections.map((s) => s.key), ...customSections.map((c) => `custom:${c.id}`)];
    const kept = (secOrder || []).filter((k) => valid.includes(k));
    valid.forEach((k) => { if (!kept.includes(k)) kept.push(k); });
    return kept;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secOrder, customSections, showPortfolio]);

  const isOn = (k) => (sel[k] === undefined ? true : sel[k]);
  const toggle = (k) => setSel((s) => ({ ...s, [k]: !(s[k] === undefined ? true : s[k]) }));

  const move = (k, dir) => {
    const arr = [...orderedKeys];
    const i = arr.indexOf(k), j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setSecOrder(arr); persist("order", arr);
  };
  const addCustom = () => {
    const c = { id: String(Date.now()), title: "", text: "" };
    const next = [...customSections, c];
    setCustomSections(next); persist("custom", next);
    const arr = [...orderedKeys, `custom:${c.id}`];
    setSecOrder(arr); persist("order", arr);
    setEditMode(true);
    toast.success("Text section added — write in it directly in the preview");
  };
  const updateCustom = (id, patch) => {
    const next = customSections.map((c) => (c.id === id ? { ...c, ...patch } : c));
    setCustomSections(next); persist("custom", next);
  };
  const removeCustom = (id) => {
    const next = customSections.filter((c) => c.id !== id);
    setCustomSections(next); persist("custom", next);
  };
  const setOverride = (id, text) => {
    const next = { ...overrides, [id]: text };
    setOverrides(next); persist("ov", next);
  };
  const resetOverrides = () => { setOverrides({}); persist("ov", {}); toast.success("All text edits reset"); };
  const toggleExecHidden = (h) => {
    const next = execHidden.includes(h) ? execHidden.filter((x) => x !== h) : [...execHidden, h];
    setExecHidden(next); persist("exechide", next);
  };
  const toggleMinute = (id) => {
    const next = selMinutes.includes(id) ? selMinutes.filter((x) => x !== id) : [...selMinutes, id];
    setSelMinutes(next); persist("minutes", next);
  };
  const toggleIncludeDone = () => {
    const next = !includeDone;
    setIncludeDone(next); persist("incdone", next);
  };
  const toggleChart = (k) => {
    const next = charts.includes(k) ? charts.filter((x) => x !== k) : [...charts, k];
    setCharts(next); persist("charts", next);
  };

  const schedTarget = () => (mode === "project" ? { kind: "project", project_id: pid } : { kind: "portfolio" });

  const toggleWeekly = async (checked) => {
    try {
      await api.post("/status-report/schedule", { ...schedTarget(), enabled: checked, day: 0 });
      setAutoWeekly(checked);
      toast.success(checked ? "Weekly auto-email enabled (every Monday)" : "Weekly auto-email turned off");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update schedule"); }
  };

  const emailReport = async () => {
    const list = recipients.split(",").map((r) => r.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Add at least one recipient email"); return; }
    setEmailing(true);
    try {
      const base = (isPortfolio || multiPick)
        ? ((multiPick || scope === "custom") ? { kind: "portfolio", project_ids: selProjects } : { kind: "portfolio", scope })
        : { kind: "project", project_id: pid };
      const { data } = await api.post("/status-report/email", { ...base, recipients: list, title, period });
      toast.success(`Read-out emailed to ${data.sent} recipient(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not email report"); }
    finally { setEmailing(false); }
  };

  const createShare = async () => {
    setSharing(true);
    try {
      const body = (isPortfolio || multiPick)
        ? ((multiPick || scope === "custom") ? { kind: "portfolio", project_ids: selProjects } : { kind: "portfolio", scope })
        : { kind: "project", project_id: pid };
      const { data } = await api.post("/status-report/share", body);
      const url = `${window.location.origin}/shared/report/${data.token}`;
      setShareUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true); setTimeout(() => setCopied(false), 2000);
        toast.success("Public link ready — copied to clipboard");
      } catch (err) {
        console.warn("Clipboard copy failed", err);
        toast.info("Public link ready — use the copy button below");
      }
    } catch (e) { toast.error("Could not create share link"); }
    finally { setSharing(false); }
  };

  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("project");
    api.get("/projects").then(({ data }) => {
      setProjects(data);
      const pre = wanted && data.find((p) => p.id === wanted);
      if (pre) { setMode("project"); setPid(pre.id); setSelProjects([pre.id]); }
      else if (data[0]) { setPid(data[0].id); setSelProjects([data[0].id]); }
    }).catch(() => {});
  }, []);

  // Single project load (exactly one selected in project mode)
  useEffect(() => {
    if (mode !== "project" || selProjects.length !== 1) return;
    const id = selProjects[0];
    setPid(id);
    const p = projects.find((x) => x.id === id);
    setTitle(`${p?.name || "Project"} — Status Report`);
    setLoading(true);
    Promise.all([
      api.get(`/projects/${id}/tasks`).then((r) => r.data).catch(() => []),
      api.get(`/projects/${id}/critical-path`).then((r) => r.data).catch(() => ({ tasks: [], path: [] })),
      api.get(`/projects/${id}/risks`).then((r) => r.data).catch(() => []),
      api.get(`/projects/${id}/action-items`).then((r) => r.data).catch(() => []),
      api.get(`/projects/${id}/budget`).then((r) => r.data).catch(() => null),
      api.get(`/projects/${id}/exec-summary`).then((r) => r.data).catch(() => null),
      api.get(`/projects/${id}/decisions`).then((r) => r.data).catch(() => []),
      api.get(`/projects/${id}/meetings`).then((r) => r.data).catch(() => []),
      api.get(`/projects/${id}/approvals`).then((r) => r.data).catch(() => []),
      api.get(`/projects/${id}/radar`).then((r) => r.data).catch(() => ({ items: [], count: 0, due_count: 0 })),
    ]).then(([tasks, cp, risks, actions, budget, exec, decisions, meetings, approvals, radar]) => {
      const healthById = Object.fromEntries((cp.tasks || []).map((t) => [t.id, t]));
      setData({ project: p, tasks, cp, healthById, risks, actions, budget, exec, decisions, meetings, approvals, radar });
      setLoading(false);
    });
  }, [selProjects, projects, mode]);

  // Portfolio load (portfolio mode, or 2+ projects picked in project mode)
  useEffect(() => {
    const multi = mode === "project" && selProjects.length > 1;
    if (mode !== "portfolio" && !multi) return;
    const custom = multi || scope === "custom";
    if (custom && selProjects.length === 0) { setPdata(null); return; }
    setTitle(custom
      ? `Portfolio Status Report — ${selProjects.length} project${selProjects.length === 1 ? "" : "s"}`
      : `Portfolio Status Report — ${scope === "all" ? "All Projects" : "Active Projects"}`);
    setLoading(true);
    setPexec(null);
    const url = custom ? `/portfolio/status-report?project_ids=${selProjects.join(",")}` : `/portfolio/status-report?scope=${scope}`;
    api.get(url).then((r) => { setPdata(r.data); setLoading(false); })
      .catch(() => { toast.error("Could not load portfolio report"); setLoading(false); });
  }, [mode, scope, selProjects]);

  // Prefill saved recipients + weekly schedule for the current target
  useEffect(() => {
    const q = mode === "project" ? (pid ? `?kind=project&project_id=${pid}` : null) : "?kind=portfolio";
    if (q === null) return;
    api.get(`/status-report/schedule${q}`).then(({ data }) => {
      setRecipients((data.recipients || []).join(", "));
      setAutoWeekly(!!data.enabled);
    }).catch(() => {});
  }, [mode, pid]);

  const stats = useMemo(() => {
    if (!data) return null;
    const cpTasks = (data.cp.tasks || []).filter((t) => !t.is_summary);
    const buckets = { blue: 0, green: 0, yellow: 0, red: 0, gray: 0 };
    cpTasks.forEach((t) => { buckets[t.health] = (buckets[t.health] || 0) + 1; });
    const avg = data.cp.avg_percent ?? progressPct(data.tasks || [], data.project?.progress_style);
    const today = new Date().toISOString().slice(0, 10);
    const overdue = data.tasks.filter((t) => !t.is_summary && t.end_date && t.end_date < today && t.status !== "done").length;
    return { buckets, avg, overdue, total: cpTasks.length };
  }, [data]);

  const [rmMode, setRmMode] = useState(() => localStorage.getItem("pf_report_rm_mode") || "roadmap");
  const [rmZoom, setRmZoom] = useState(() => Number(localStorage.getItem("pf_report_rm_zoom")) || 100);
  const setRm = (m) => { setRmMode(m); localStorage.setItem("pf_report_rm_mode", m); };

  const saveProjPatch = async (fields) => {
    const p = data.project;
    try {
      const { data: upd } = await api.put(`/projects/${p.id}`, {
        name: p.name, description: p.description || "", industry: p.industry || "",
        trueup_frequency: p.trueup_frequency || null, trueup_custom_days: p.trueup_custom_days ?? null,
        start_date: p.start_date || null, end_date: p.end_date || null,
        work_on_weekends: !!p.work_on_weekends, timezone: p.timezone || null,
        duration_unit: p.duration_unit || null, holiday_calendar: p.holiday_calendar || null,
        custom_holidays: p.custom_holidays || null, phase_colors: p.phase_colors || null,
        flow_colors: p.flow_colors || null, summary_color: p.summary_color || null,
        report_lane_order: p.report_lane_order || null, program: p.program || null, ...fields,
      });
      setData((d) => ({ ...d, project: upd }));
    } catch { toast.error("Could not save the roadmap change"); }
  };

  const moveReportLane = (ph, dir, visible) => {
    const order = [...visible];
    const i = order.indexOf(ph);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    saveProjPatch({ report_lane_order: order });
  };
  const setZoom = (z) => { setRmZoom(z); localStorage.setItem("pf_report_rm_zoom", String(z)); };

  const generateExec = async () => {
    setGenExec(true);
    try {
      const { data: res } = await api.post(`/projects/${pid}/ai/executive-summary`);
      setData((d) => ({ ...d, exec: { ...d.exec, summary: res.summary, generated_at: res.generated_at, is_due: false } }));
      toast.success("Executive summary generated");
    } catch (e) { toast.error("Could not generate summary"); }
    finally { setGenExec(false); }
  };

  const generatePortfolioExec = async () => {
    setGenExec(true);
    try {
      const q = (multiPick || scope === "custom") ? `project_ids=${selProjects.join(",")}` : `scope=${scope}`;
      const { data: res } = await api.post(`/portfolio/ai/executive-summary?${q}`);
      setPexec(res.summary);
      toast.success("Portfolio summary generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate summary"); }
    finally { setGenExec(false); }
  };

  const phaseRows = useMemo(() => {
    if (!data) return [];
    const map = {};
    (data.tasks || []).forEach((t) => {
      const p = t.phase || "Unphased";
      if (!map[p]) map[p] = { phase: p, items: [] };
      map[p].items.push(t);
    });
    return Object.values(map)
      .sort((a, b) => {
        const order = derivePhases(data.project, data.tasks);
        const idx = (p) => { const i = order.indexOf(p); return i === -1 ? order.length : i; };
        return idx(a.phase) - idx(b.phase);
      })
      .map((m) => ({ phase: m.phase, pct: progressPct(m.items, data.project?.progress_style), n: m.items.filter((t) => !t.is_summary).length }));
  }, [data]);

  const bucketFor = (status, due) => (status === "done" ? "completed" : (due && due < new Date().toISOString().slice(0, 10) ? "late" : "pending"));
  const markAction = async (a, checked) => {
    const status = checked ? "done" : "open";
    try {
      await api.patch(`/action-items/${a.id}/status`, { status });
      const upd = (arr) => (arr || []).map((x) => (x.id === a.id ? { ...x, status, bucket: bucketFor(status, x.due_date) } : x));
      setData((d) => (d ? { ...d, actions: upd(d.actions) } : d));
      setPdata((d) => (d ? { ...d, actions: upd(d.actions) } : d));
      toast.success(checked ? "Action item marked complete" : "Action item reopened");
    } catch (e) { toast.error("Could not update the action item"); }
  };

  const execMd = showPortfolio ? pexec : (overrides.exec !== undefined ? overrides.exec : data?.exec?.summary);
  const minutesMeetings = (data?.meetings || []).filter((m) => (m.minutes || "").trim());
  const displayKeys = orderedKeys.filter((k) => isOn(k));

  // ---------------- single-project section renderers ----------------
  const renderProjectSection = (k) => {
    if (k.startsWith("custom:")) {
      const cs = customSections.find((c) => `custom:${c.id}` === k);
      return cs ? <CustomSectionBlock key={k} cs={cs} editMode={editMode} onUpdate={updateCustom} /> : null;
    }
    switch (k) {
      case "exec":
        return (
          <Section title="Executive Summary" key={k}>
            {data.exec?.summary ? (
              <>
                {editMode ? (
                  <Textarea rows={10} className="text-sm" value={overrides.exec !== undefined ? overrides.exec : data.exec.summary} onChange={(e) => setOverride("exec", e.target.value)} data-testid="exec-edit-textarea" />
                ) : (
                  <div className="text-sm text-foreground [&_h2]:text-primary [&_h3]:text-primary [&_h4]:text-primary [&_h2]:font-bold [&_h3]:font-bold [&_h4]:font-bold" data-testid="exec-summary-body">
                    <ExecFiltered md={execMd} hidden={execHidden} />
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3 print-hide">
                  <p className="text-[11px] text-muted-foreground" data-testid="exec-summary-generated-at">
                    Generated {data.exec.generated_at ? new Date(data.exec.generated_at).toLocaleString() : "—"}{data.exec.is_due ? " · counts may be out of date" : ""}
                  </p>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] gap-1 text-primary" onClick={generateExec} disabled={genExec} data-testid="report-refresh-exec">
                    {genExec ? <Loader2 className="h-3 w-3 animate-spin" /> : <Presentation className="h-3 w-3" />} Refresh with current data
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground flex items-center gap-3 print-hide">
                No AI summary yet.
                <Button size="sm" variant="outline" className="gap-2" onClick={generateExec} disabled={genExec} data-testid="report-gen-exec"><Presentation className="h-4 w-4" />{genExec ? "Generating…" : "Generate"}</Button>
              </div>
            )}
          </Section>
        );
      case "health":
        if (!stats) return null;
        return (
          <Section title="Health Overview" key={k}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Metric label="Overall progress" value={`${stats.avg}%`} color="#0044FF" onClick={() => nav(`/projects/${pid}?tab=roadmap`)} />
              <Metric label="Tasks" value={stats.total} onClick={() => nav(`/projects/${pid}?tab=plan`)} />
              <Metric label="Overdue" value={stats.overdue} color={stats.overdue ? "#EF4444" : undefined} onClick={() => nav(`/projects/${pid}?tab=reports`)} />
              <Metric label="Complete" value={stats.buckets.blue} color="#2563EB" onClick={() => nav(`/projects/${pid}?tab=reports`)} />
            </div>
            <div className="flex flex-wrap gap-4">
              {["blue", "green", "yellow", "red", "gray"].filter((x) => stats.buckets[x]).map((x) => (
                <span key={x} className="inline-flex items-center gap-1.5 text-sm"><span className="h-3 w-3 rounded-full" style={{ background: HEALTH_COLORS[x] }} /> <b>{stats.buckets[x]}</b> {HEALTH_LABELS[x]}</span>
              ))}
            </div>
            {charts.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="report-health-charts">
                {CHART_DEFS.filter((c) => charts.includes(c.key)).map((c) => (
                  <ReportChart key={c.key} kind={c.key} tasks={data.tasks} cpTasks={data.cp.tasks || []} risks={data.risks} actions={data.actions} progressStyle={data.project?.progress_style} />
                ))}
              </div>
            )}
          </Section>
        );
      case "phases":
        return (
          <Section title="Progress by Phase" key={k}>
            <div className="space-y-2">
              {phaseRows.map((r) => (
                <div key={r.phase} className="flex items-center gap-3">
                  <span className="text-sm w-40 truncate">{r.phase}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${r.pct}%` }} /></div>
                  <span className="text-sm font-semibold w-10 text-right">{r.pct}%</span>
                </div>
              ))}
            </div>
          </Section>
        );
      case "milestones":
        return (
          <Section title="Roadmap & Key Dates" key={k}>
            <div className="flex flex-wrap items-center gap-3 mb-3 print-hide">
              <div className="flex rounded-lg border border-border overflow-hidden text-xs font-semibold">
                <button className={`px-3 py-1.5 transition-colors ${rmMode === "roadmap" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary/60"}`} onClick={() => setRm("roadmap")} data-testid="report-rm-mode-roadmap">Summarized roadmap</button>
                <button className={`px-3 py-1.5 transition-colors ${rmMode === "tasks" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary/60"}`} onClick={() => setRm("tasks")} data-testid="report-rm-mode-tasks">Visual plan (tasks)</button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Timescale</span>
                <input type="range" min="100" max="300" step="10" value={rmZoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-28 accent-primary" data-testid="report-rm-zoom" />
                <span className="text-xs tabular-nums text-muted-foreground w-10">{rmZoom === 100 ? "Fit" : `${rmZoom}%`}</span>
              </div>
            </div>
            <ReportRoadmap project={data.project} tasks={data.tasks} mode={rmMode} zoom={rmZoom} healthById={data.healthById}
              onOpenTask={(t) => nav(`/projects/${data.project.id}?tab=plan&task=${t.id}`)}
              onOpenPhase={() => nav(`/projects/${data.project.id}?tab=roadmap`)}
              onColorChange={(ph, c) => saveProjPatch({ phase_colors: { ...(data.project.phase_colors || {}), [ph]: c } })}
              onMoveLane={moveReportLane} />
          </Section>
        );
      case "minutes":
        return (
          <Section title="Meeting Minutes" key={k}>
            {selMinutes.length === 0 ? (
              <Empty text="No minutes inserted — pick meetings in the sidebar (Meeting minutes to insert)." />
            ) : (
              minutesMeetings.filter((m) => selMinutes.includes(m.id)).map((m) => (
                <div key={m.id} className="mb-5 break-inside-avoid" data-testid={`report-minutes-${m.id}`}>
                  <p className="text-sm font-bold">{m.title} <span className="text-muted-foreground font-normal">· {m.meeting_date}</span></p>
                  <div className="text-sm mt-1.5 border-l-2 border-primary/20 pl-3"><Markdown text={m.minutes} /></div>
                </div>
              ))
            )}
          </Section>
        );
      case "risks": {
        const visR = data.risks.filter((r) => r.status !== "parked");
        const inProgR = visR.filter((r) => r.status !== "closed").sort((a, b) => (b.score || 0) - (a.score || 0));
        const closedR = visR.filter((r) => r.status === "closed");
        const rrow = (r) => {
          const sv = sevOf(r.score || 0);
          const closed = r.status === "closed";
          return (
            <li key={r.id} className="flex items-center gap-2.5 text-sm border-b border-border/50 pb-1.5" data-testid={`report-risk-${r.id}`}>
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: HEALTH_DOT[riskHealth(r)] }} data-testid={`report-risk-dot-${r.id}`} />
              <span className="truncate flex-1 min-w-0">
                {editMode ? <EditableLine id={`r-${r.id}`} text={r.title} editMode overrides={overrides} onOverride={setOverride} /> : (
                  <span onClick={() => nav(`/projects/${data.project.id}?tab=risks`)} title="Open risk register" className={`cursor-pointer hover:underline ${closed ? "line-through text-muted-foreground" : ""}`}>
                    {overrides[`r-${r.id}`] !== undefined ? overrides[`r-${r.id}`] : r.title} <span className="text-muted-foreground">— {r.owner || "unassigned"}</span>
                  </span>
                )}
              </span>
              {originOf(r) && <span className="text-[11px] text-muted-foreground italic shrink-0" data-testid={`report-risk-origin-${r.id}`}>{originOf(r)}</span>}
              <span className="text-[11px] font-bold text-white px-2 py-0.5 rounded shrink-0" style={{ background: closed ? "#9CA3AF" : sv.c }}>{closed ? "Closed" : `${sv.l} (${r.score})`}</span>
            </li>
          );
        };
        return (
          <Section title="Risks" key={k}>
            {visR.length === 0 ? <Empty text="No risks logged." /> : (
              <>
                <DotLegend />
                {inProgR.length > 0 && (
                  <ActionGroup id="risks-in-progress" testid="report-risks-label-in-progress" label="In Progress" color="#10B981" count={inProgR.length} defaultOpen={false}>
                    {SEVERITY_BANDS.map(([lb, col, test]) => {
                      const g = inProgR.filter((r) => test(r.score));
                      return g.length === 0 ? null : (
                        <div className="ml-3" key={lb}>
                          <ActionGroup id={`risks-${lb}`} testid={`report-risks-sev-${lb.toLowerCase()}`} label={lb} color={col} count={g.length}>
                            <ul className="space-y-1.5">{g.map(rrow)}</ul>
                          </ActionGroup>
                        </div>
                      );
                    })}
                  </ActionGroup>
                )}
                {closedR.length > 0 && (
                  <ActionGroup id="risks-closed" testid="report-risks-label-closed" label="Closed" color="#2563EB" count={closedR.length} defaultOpen={false}>
                    <ul className="space-y-1.5">{closedR.map(rrow)}</ul>
                  </ActionGroup>
                )}
              </>
            )}
          </Section>
        );
      }
      case "decisions": {
        const decisions = (data.decisions || []).filter((d) => d.status !== "parked");
        const inProgD = decisions.filter((d) => d.status !== "decided");
        const closedD = decisions.filter((d) => d.status === "decided");
        const drow = (d) => (
          <li key={d.id} className="flex items-center gap-2.5 text-sm border-b border-border/50 pb-1.5" data-testid={`report-decision-${d.id}`}>
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: HEALTH_DOT[decisionHealth(d)] }} data-testid={`report-decision-dot-${d.id}`} />
            <span className="truncate flex-1 min-w-0">
              {editMode ? <EditableLine id={`d-${d.id}`} text={d.title} editMode overrides={overrides} onOverride={setOverride} /> : (
                <span onClick={() => nav(`/projects/${data.project.id}?tab=raid`)} title="Open RAID register" className={`cursor-pointer hover:underline ${d.status === "decided" ? "line-through text-muted-foreground" : ""}`}>
                  {overrides[`d-${d.id}`] !== undefined ? overrides[`d-${d.id}`] : d.title} <span className="text-muted-foreground">— {d.owner || "unassigned"}</span>
                </span>
              )}
            </span>
            {d.blocking && d.status !== "decided" && <span className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded bg-red-500 shrink-0">BLOCKING</span>}
            {originOf(d) && <span className="text-[11px] text-muted-foreground italic shrink-0" data-testid={`report-decision-origin-${d.id}`}>{originOf(d)}</span>}
            <span className="text-muted-foreground shrink-0">{d.status === "decided" ? "decided" : (d.due_date ? `needed by ${d.due_date}` : "open")}</span>
          </li>
        );
        return (
          <Section title="Decisions" key={k}>
            {decisions.length === 0 ? <Empty text="No decision points logged." /> : (
              <>
                <DotLegend />
                {inProgD.length > 0 && (
                  <ActionGroup id="decisions-in-progress" testid="report-decisions-label-in-progress" label="In Progress" color="#10B981" count={inProgD.length} defaultOpen={false}>
                    {[["Blocking", "#EF4444", (d) => !!d.blocking], ["Non-blocking", "#10B981", (d) => !d.blocking]].map(([lb, col, test]) => {
                      const g = inProgD.filter(test);
                      return g.length === 0 ? null : (
                        <div className="ml-3" key={lb}>
                          <ActionGroup id={`decisions-${lb}`} testid={`report-decisions-cat-${lb.toLowerCase()}`} label={lb} color={col} count={g.length}>
                            <ul className="space-y-1.5">{g.map(drow)}</ul>
                          </ActionGroup>
                        </div>
                      );
                    })}
                  </ActionGroup>
                )}
                {closedD.length > 0 && (
                  <ActionGroup id="decisions-closed" testid="report-decisions-label-closed" label="Closed" color="#2563EB" count={closedD.length} defaultOpen={false}>
                    <ul className="space-y-1.5">{closedD.map(drow)}</ul>
                  </ActionGroup>
                )}
              </>
            )}
          </Section>
        );
      }
      case "ioflow":
        return (
          <Section title="IO Flow — Input → Tasks → Output" key={k}>
            <ReportFlowStrip phases={data.project.phases || []} tasks={data.tasks} phaseColors={data.project.phase_colors || {}} progressStyle={data.project?.progress_style} />
          </Section>
        );
      case "radar": {
        const radarItems = data.radar?.items || [];
        const todayIso = new Date().toISOString().slice(0, 10);
        return (
          <Section title="On the Radar — Parked / On Hold" key={k}>
            {radarItems.length === 0 ? <Empty text="Nothing is parked right now." /> : (
              <ul className="space-y-1.5">
                {radarItems.map((it) => (
                  <li key={`${it.type}-${it.id}`} className="flex items-center gap-2.5 text-sm border-b border-border/50 pb-1.5" data-testid={`report-radar-${it.id}`}>
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: it.revisit && it.revisit <= todayIso ? "#EF4444" : it.due_soon ? "#F59E0B" : "#0EA5E9" }} />
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 shrink-0">{it.type}</span>
                    <span onClick={() => nav(`/projects/${data.project.id}?tab=radar`)} title="Open On the Radar" className="truncate flex-1 min-w-0 cursor-pointer hover:underline">
                      {it.title} <span className="text-muted-foreground">— {it.owner || "unassigned"}</span>
                      {it.reason && <span className="text-muted-foreground italic"> · “{it.reason}”</span>}
                    </span>
                    <span className={`text-[11px] shrink-0 font-semibold ${it.due ? "text-red-600" : it.due_soon ? "text-amber-600" : "text-muted-foreground"}`}>
                      {it.revisit ? `revisit ${it.revisit}${it.due ? " (due)" : ""}` : "no revisit date"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        );
      }
      case "approvals": {
        const apps = data.approvals || [];
        const pendingAp = apps.filter((x) => x.status === "pending");
        const doneAp = apps.filter((x) => x.status !== "pending");
        const today = new Date().toISOString().slice(0, 10);
        const arow = (x) => {
          const approved = (x.approvers || []).filter((ap) => ap.status === "approved").length;
          const total = (x.approvers || []).length;
          const late = x.status === "pending" && x.due_date && x.due_date < today;
          return (
            <li key={x.id} className="flex items-center gap-2.5 text-sm border-b border-border/50 pb-1.5" data-testid={`report-approval-${x.id}`}>
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: x.status === "approved" ? "#2563EB" : x.status === "rejected" ? "#EF4444" : late ? "#EF4444" : "#F59E0B" }} />
              <span className="font-mono text-[10px] font-bold text-primary shrink-0">{x.display_id}</span>
              <span onClick={() => nav(`/projects/${data.project.id}?tab=approvals`)} title="Open the approvals library" className={`truncate flex-1 min-w-0 cursor-pointer hover:underline ${x.status !== "pending" ? "text-muted-foreground" : ""}`}>
                {x.title} <span className="text-muted-foreground">— {x.doc_type}</span>
              </span>
              <span className="text-[11px] text-muted-foreground shrink-0">{approved}/{total} approved{x.due_date ? ` · due ${x.due_date}` : ""}</span>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full shrink-0 ${x.status === "approved" ? "bg-emerald-100 text-emerald-700" : x.status === "rejected" ? "bg-red-100 text-red-700" : late ? "bg-red-500 text-white" : "bg-amber-100 text-amber-700"}`}>{late ? "overdue" : x.status}</span>
            </li>
          );
        };
        return (
          <Section title="Document Approvals" key={k}>
            {apps.length === 0 ? <Empty text="No approval requests yet — start one under Library → Approvals." /> : (
              <>
                {pendingAp.length > 0 && (
                  <ActionGroup id="approvals-pending" testid="report-approvals-label-pending" label="Pending" color="#F59E0B" count={pendingAp.length} defaultOpen={false}>
                    <ul className="space-y-1.5">{pendingAp.map(arow)}</ul>
                  </ActionGroup>
                )}
                {doneAp.length > 0 && (
                  <ActionGroup id="approvals-resolved" testid="report-approvals-label-resolved" label="Resolved" color="#2563EB" count={doneAp.length} defaultOpen={false}>
                    <ul className="space-y-1.5">{doneAp.map(arow)}</ul>
                  </ActionGroup>
                )}
              </>
            )}
          </Section>
        );
      }
      case "actions": {
        const acts = (includeDone ? data.actions : data.actions.filter((a) => a.bucket !== "completed")).filter((a) => a.status !== "parked");
        const openA = acts.filter((a) => a.bucket !== "completed");
        const doneA = acts.filter((a) => a.bucket === "completed");
        const row = (a) => (
          <li key={a.id} className="flex items-center gap-2.5 text-sm border-b border-border/50 pb-1.5" data-testid={`report-action-${a.id}`}>
            <input type="checkbox" className="accent-primary h-4 w-4 shrink-0 cursor-pointer print-hide" checked={a.bucket === "completed"} onChange={(e) => markAction(a, e.target.checked)} title={a.bucket === "completed" ? "Reopen" : "Mark complete"} data-testid={`report-action-check-${a.id}`} />
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: HEALTH_DOT[actionHealth(a)] }} data-testid={`report-action-dot-${a.id}`} />
            {a.display_id && <span className="font-mono text-[10px] font-bold text-primary shrink-0" data-testid={`report-action-id-${a.id}`}>{a.display_id}</span>}
            <span className="truncate flex-1 min-w-0">
              {editMode ? <><EditableLine id={`a-${a.id}`} text={a.title} editMode overrides={overrides} onOverride={setOverride} /><span className="text-muted-foreground"> — {a.owner || "unassigned"}</span></> : (
                <span onClick={() => nav(`/projects/${data.project.id}?tab=actions`)} title="Open action items register" className={`cursor-pointer hover:underline ${a.bucket === "completed" ? "line-through text-muted-foreground" : ""}`}>
                  {overrides[`a-${a.id}`] !== undefined ? overrides[`a-${a.id}`] : a.title} <span className="text-muted-foreground">— {a.owner || "unassigned"}</span>
                </span>
              )}
            </span>
            {originOf(a) && <span className="text-[11px] text-muted-foreground italic shrink-0" data-testid={`report-action-origin-${a.id}`}>{originOf(a)}</span>}
            <span className="text-muted-foreground shrink-0">{a.due_date || "—"}{a.bucket === "late" ? " · late" : ""}</span>
          </li>
        );
        return (
          <Section title="Action Items" key={k}>
            {acts.length === 0 ? <Empty text={includeDone ? "No action items." : "No open action items."} /> : (
              <>
                <div className="flex gap-4 mb-2 text-sm">
                  {["late", "pending", "completed"].filter((b) => includeDone || b !== "completed").map((b) => (
                    <span key={b} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: b === "late" ? "#EF4444" : b === "pending" ? "#F59E0B" : "#2563EB" }} /> <b>{acts.filter((a) => a.bucket === b).length}</b> {b}</span>
                  ))}
                </div>
                <DotLegend />
                {openA.length > 0 && (
                  <ActionGroup id="in-progress" testid="report-actions-label-in-progress" label="In Progress" color="#10B981" count={openA.length} defaultOpen={false}>
                    <ul className="space-y-1.5">{openA.map(row)}</ul>
                  </ActionGroup>
                )}
                {doneA.length > 0 && (
                  <ActionGroup id="closed" testid="report-actions-label-closed" label="Closed" color="#2563EB" count={doneA.length} defaultOpen={false}>
                    <ul className="space-y-1.5">{doneA.map(row)}</ul>
                  </ActionGroup>
                )}
              </>
            )}
          </Section>
        );
      }
      case "blockers": {
        const blocked = data.tasks.filter((t) => t.status === "blocked");
        return (
          <Section title="Blockers" key={k}>
            {blocked.length === 0 ? <Empty text="No blockers 🎉" /> : (
              <ul className="space-y-1.5">
                {blocked.map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-2 text-sm border-b border-border/50 pb-1.5" data-testid={`report-blocker-${t.id}`}>
                    <span className="flex-1 min-w-0 truncate">
                      {editMode ? <EditableLine id={`b-${t.id}`} text={t.title} editMode overrides={overrides} onOverride={setOverride} /> : (
                        <span onClick={() => nav(`/projects/${data.project.id}?tab=plan&task=${t.id}`)} title="Open in plan" className="cursor-pointer hover:underline">{overrides[`b-${t.id}`] !== undefined ? overrides[`b-${t.id}`] : t.title}</span>
                      )}
                    </span>
                    <span className="text-muted-foreground shrink-0">{t.assignee || "unassigned"} · {t.phase}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        );
      }
      case "budget":
        if (!data.budget) return null;
        return (
          <Section title="Budget Snapshot" key={k}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Metric label="Budget" value={data.budget.budget != null ? money(data.budget.budget, data.budget.currency) : "—"} />
              <Metric label="Planned cost" value={money(data.budget.planned_cost, data.budget.currency)} />
              <Metric label="Variance" value={data.budget.budget_variance == null ? "—" : money(data.budget.budget_variance, data.budget.currency)} color={data.budget.budget_variance < 0 ? "#EF4444" : "#10B981"} />
              <Metric label="Used" value={data.budget.percent_used == null ? "—" : `${data.budget.percent_used}%`} />
            </div>
          </Section>
        );
      default:
        return null;
    }
  };

  const downloadPdf = async () => {
    const el = document.getElementById("report-preview");
    if (!el) return;
    setPdfBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 60));
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
      const pdf = new jsPDF({ unit: "pt", format: "letter" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 30;
      const footerY = ph - 16;
      const imgW = pw - margin * 2;
      const contentH = ph - margin * 2 - 14;
      const pxPerPt = canvas.width / imgW;
      const pagePx = Math.floor(contentH * pxPerPt);
      const pages = Math.max(1, Math.ceil(canvas.height / pagePx));
      const footer = title || "Status Report";
      for (let i = 0; i < pages; i++) {
        if (i > 0) pdf.addPage();
        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = Math.min(pagePx, canvas.height - i * pagePx);
        slice.getContext("2d").drawImage(canvas, 0, i * pagePx, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
        pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", margin, margin, imgW, slice.height / pxPerPt);
        pdf.setFontSize(8);
        pdf.setTextColor(120);
        pdf.text(footer, margin, footerY);
        pdf.text(`Page ${i + 1} of ${pages}`, pw - margin, footerY, { align: "right" });
      }
      pdf.save(`${(title || "status-report").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not build the PDF — try Print instead");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6 print-hide">
        <p className="overline text-muted-foreground mb-2">Reporting</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3"><FileBarChart className="h-9 w-9 text-primary" /> Status Report Builder</h1>
        <p className="text-muted-foreground mt-2">Report on a single project or roll up your whole portfolio, then print or share.</p>
        <SeeItInAction chapter={11} className="mt-2" />
      </div>

      <div className={`print-grid grid gap-6 ${panelOpen ? "lg:grid-cols-[320px_1fr]" : ""}`}>
        {/* Controls */}
        {!panelOpen && (
          <div className="print-hide">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setPanelOpen(true)} data-testid="report-show-options">
              <PanelLeftOpen className="h-4 w-4" /> Show report options
            </Button>
          </div>
        )}
        <div className={`print-hide space-y-5 lg:sticky lg:top-20 self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-2 ${panelOpen ? "" : "hidden"}`} data-testid="report-controls">
          <Button variant="ghost" size="sm" className="gap-2 -ml-2 text-muted-foreground" onClick={() => setPanelOpen(false)} title="Hide the options panel for a clean, presentable report" data-testid="report-hide-options">
            <PanelLeftClose className="h-4 w-4" /> Hide options (presentation mode)
          </Button>
          <div>
            <Label>Report scope</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1.5" data-testid="report-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="project" data-testid="report-mode-project">Specific project(s)</SelectItem>
                <SelectItem value="portfolio" data-testid="report-mode-portfolio">All projects (Portfolio)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isPortfolio ? (
            <div className="space-y-3">
              <div>
                <Label>Include projects</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className="mt-1.5" data-testid="report-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active" data-testid="report-scope-active">Active / in-progress only</SelectItem>
                    <SelectItem value="all" data-testid="report-scope-all">All projects</SelectItem>
                    <SelectItem value="custom" data-testid="report-scope-custom">Select specific projects…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {scope === "custom" && (
                <div className="border border-border rounded-lg p-3 space-y-2 max-h-56 overflow-y-auto" data-testid="report-project-multiselect">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{selProjects.length} of {projects.length} selected</span>
                    <button type="button" className="text-xs text-primary font-medium" onClick={() => setSelProjects(selProjects.length === projects.length ? [] : projects.map((p) => p.id))} data-testid="report-project-selectall">
                      {selProjects.length === projects.length ? "Clear all" : "Select all"}
                    </button>
                  </div>
                  {projects.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`report-project-check-${p.id}`}>
                      <Checkbox checked={selProjects.includes(p.id)} onCheckedChange={() => setSelProjects((s) => s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id])} /> {p.name}
                    </label>
                  ))}
                  {projects.length === 0 && <p className="text-xs text-muted-foreground">No projects yet.</p>}
                </div>
              )}
            </div>
          ) : (
            <div>
              <Label>Projects</Label>
              <div className="mt-1.5 border border-border rounded-lg p-3 space-y-2 max-h-56 overflow-y-auto" data-testid="report-project-multiselect-single">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{selProjects.length} of {projects.length} selected</span>
                  <button type="button" className="text-xs text-primary font-medium" onClick={() => setSelProjects(selProjects.length === projects.length ? [] : projects.map((p) => p.id))} data-testid="report-project-selectall-single">
                    {selProjects.length === projects.length ? "Clear all" : "Select all"}
                  </button>
                </div>
                {projects.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`report-project-pick-${p.id}`}>
                    <Checkbox checked={selProjects.includes(p.id)} onCheckedChange={() => setSelProjects((s) => s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id])} /> {p.name}
                  </label>
                ))}
                {projects.length === 0 && <p className="text-xs text-muted-foreground">No projects yet.</p>}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Pick one for a detailed report, or several for a combined roll-up.</p>
            </div>
          )}

          <div>
            <Label htmlFor="rp-title">Report title</Label>
            <Input id="rp-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" data-testid="report-title-input" />
          </div>
          <div>
            <Label htmlFor="rp-period">Reporting date</Label>
            <Input id="rp-period" type="date" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1.5" data-testid="report-period-input" />
          </div>

          <div>
            <Label className="mb-2 block">Sections — check to include, arrows to reorder</Label>
            <div className="space-y-1.5 border border-border rounded-lg p-3" data-testid="report-sections-box">
              {orderedKeys.map((k, i) => {
                const meta = SECTIONS.find((s) => s.key === k);
                const cs = k.startsWith("custom:") ? customSections.find((c) => `custom:${c.id}` === k) : null;
                if (!meta && !cs) return null;
                return (
                  <div key={k} className="flex items-center gap-1" data-testid={`report-secrow-${k}`}>
                    <label className="flex items-center gap-2 text-sm cursor-pointer flex-1 min-w-0" data-testid={`report-toggle-${k}`}>
                      <Checkbox checked={isOn(k)} onCheckedChange={() => toggle(k)} />
                      <span className="truncate">{cs ? (cs.title || "Text section") : meta.label}</span>
                    </label>
                    {cs && (
                      <button type="button" className="text-muted-foreground hover:text-destructive p-0.5" onClick={() => removeCustom(cs.id)} title="Delete this text section" data-testid={`report-remove-custom-${cs.id}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button type="button" disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-25 p-0.5" onClick={() => move(k, -1)} title="Move up" data-testid={`report-moveup-${k}`}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" disabled={i === orderedKeys.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-25 p-0.5" onClick={() => move(k, 1)} title="Move down" data-testid={`report-movedown-${k}`}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
            <Button variant="outline" size="sm" className="gap-2 w-full mt-2" onClick={addCustom} data-testid="report-add-text-section"><Plus className="h-4 w-4" /> Add free-form text section</Button>
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-3" data-testid="report-include-done-toggle">
              <Checkbox checked={includeDone} onCheckedChange={toggleIncludeDone} />
              <span>Include completed (closed) action items</span>
            </label>
          </div>

          {!showPortfolio && isOn("health") && (
            <div>
              <Label className="mb-2 block">Health overview graphs (from Reports)</Label>
              <div className="space-y-1.5 border border-border rounded-lg p-3" data-testid="report-charts-picker">
                {CHART_DEFS.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`report-chart-pick-${c.key}`}>
                    <Checkbox checked={charts.includes(c.key)} onCheckedChange={() => toggleChart(c.key)} /> {c.label}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Checked graphs appear inside the Health Overview section.</p>
            </div>
          )}

          {isOn("exec") && execMd && (
            <div>
              <Label className="mb-2 block">Executive summary components</Label>
              <div className="space-y-1.5 border border-border rounded-lg p-3 max-h-44 overflow-y-auto" data-testid="report-exec-components">
                {execHeadings(execMd).length === 0 ? <p className="text-xs text-muted-foreground">No components detected.</p> :
                  execHeadings(execMd).map((h) => (
                    <label key={h} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`report-exec-comp-${h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <Checkbox checked={!execHidden.includes(h)} onCheckedChange={() => toggleExecHidden(h)} /> <span className="truncate">{h}</span>
                    </label>
                  ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Uncheck a component (e.g. Outlook) to drop it from the summary.</p>
            </div>
          )}

          {!showPortfolio && isOn("minutes") && (
            <div>
              <Label className="mb-2 block">Meeting minutes to insert</Label>
              <div className="space-y-1.5 border border-border rounded-lg p-3 max-h-44 overflow-y-auto" data-testid="report-minutes-picker">
                {minutesMeetings.length === 0 ? <p className="text-xs text-muted-foreground">No meetings with minutes yet — add them under Communicate → Meetings.</p> :
                  minutesMeetings.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`report-minutes-pick-${m.id}`}>
                      <Checkbox checked={selMinutes.includes(m.id)} onCheckedChange={() => toggleMinute(m.id)} /> <span className="truncate">{m.title} · {m.meeting_date}</span>
                    </label>
                  ))}
              </div>
            </div>
          )}

          <div className="border border-border rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Edit report content</p>
                <p className="text-[11px] text-muted-foreground">Reword line items & summary in the preview. Edits only change this report.</p>
              </div>
              <Switch checked={editMode} onCheckedChange={setEditMode} data-testid="report-edit-toggle" />
            </div>
            {Object.keys(overrides).length > 0 && (
              <button type="button" className="text-xs text-primary font-medium mt-2" onClick={resetOverrides} data-testid="report-reset-overrides">Reset all text edits ({Object.keys(overrides).length})</button>
            )}
          </div>

          <div className="flex gap-2">
            <Button className="gap-2 flex-1" onClick={downloadPdf} disabled={pdfBusy} data-testid="report-download-pdf-button">
              {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
            </Button>
            <Button variant="outline" className="gap-2 flex-1" onClick={() => window.print()} data-testid="report-print-button"><Printer className="h-4 w-4" /> Print</Button>
            <Button variant="outline" className="gap-2 flex-1" onClick={createShare} disabled={sharing || nonePicked || (isPortfolio && scope === "custom" && selProjects.length === 0)} data-testid="report-share-button">
              {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share link
            </Button>
          </div>
          {shareUrl && (
            <div className="border border-border rounded-lg p-3 bg-secondary/30" data-testid="share-url-box">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Public read-only link</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={shareUrl} className="text-xs h-9" data-testid="share-url-input" onFocus={(e) => e.target.select()} />
                <Button size="icon" variant="ghost" className="shrink-0 h-9 w-9" onClick={async () => { try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (err) { console.warn("Clipboard copy failed", err); toast.error("Could not copy — select the link text and copy manually"); } }} data-testid="share-url-copy">
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Anyone with this link can view this report without signing in.</p>
            </div>
          )}

          {/* Email read-out to stakeholders */}
          <div className="border border-border rounded-lg p-3" data-testid="email-report-box">
            <Label htmlFor="rp-recipients" className="text-[11px] uppercase tracking-wide text-muted-foreground">Email read-out to stakeholders</Label>
            <Input id="rp-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="alex@co.com, sam@co.com" className="mt-1.5 text-sm h-9" data-testid="report-recipients-input" />
            <Button variant="outline" className="gap-2 w-full mt-2" onClick={emailReport} disabled={emailing || !recipients.trim() || nonePicked || (isPortfolio && scope === "custom" && selProjects.length === 0)} data-testid="report-email-button">
              {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email read-out
            </Button>
            <p className="text-[11px] text-muted-foreground mt-1.5">Comma-separated. Recipients are saved for next time.</p>
            {!(isPortfolio && scope === "custom") && !multiPick && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <div>
                  <p className="text-sm font-medium">Email automatically every week</p>
                  <p className="text-[11px] text-muted-foreground">Sends this read-out to the saved recipients every Monday.</p>
                </div>
                <Switch checked={autoWeekly} onCheckedChange={toggleWeekly} data-testid="report-weekly-toggle" />
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="border border-border rounded-lg p-8 bg-white" id="report-preview" data-testid="report-preview">
          {!nonePicked && !loading && (showPortfolio ? pdata : data) && (
            <div className="hidden print-cover flex-col items-center justify-center text-center" data-testid="report-print-cover">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-muted-foreground">Status Report</p>
              <h1 className="font-display text-5xl font-extrabold tracking-tight mt-4">{title}</h1>
              <div className="h-1 w-24 rounded-full bg-primary mt-6 mb-6" />
              <p className="text-lg font-semibold">{showPortfolio ? `Portfolio — ${pdata?.projects?.length || 0} project${(pdata?.projects?.length || 0) === 1 ? "" : "s"}` : (title.includes(data?.project?.name) ? "" : data?.project?.name)}</p>
              <p className="text-sm text-muted-foreground mt-2">Reporting period: {period}</p>
              <p className="text-sm text-muted-foreground mt-10">Prepared by {user?.name} · {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">PM Orchestrator</p>
            </div>
          )}
          {nonePicked ? (
            <p className="text-sm text-muted-foreground py-24 text-center" data-testid="report-none-picked">Select at least one project to build a report.</p>
          ) : loading || (showPortfolio ? !pdata : !data) ? (
            <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : showPortfolio ? (
            <PortfolioReport pdata={pdata} pexec={pexec} order={displayKeys} title={title} period={period} user={user}
              genExec={genExec} onGenerate={generatePortfolioExec} onToggleAction={markAction} onNavigate={(path) => nav(path)} onOpenProject={(id) => nav(`/projects/${id}?tab=roadmap`)}
              customSections={customSections} editMode={editMode} onUpdateCustom={updateCustom} execHidden={execHidden} includeDone={includeDone} />
          ) : (
            <div className="space-y-8">
              <div className="border-b border-border pb-4">
                <h2 className="font-display text-3xl font-extrabold tracking-tight">{title}</h2>
                <p className="text-sm text-muted-foreground mt-1">{data.project?.industry ? `${data.project.industry} · ` : ""}Reporting date {period} · Prepared by {user?.name}</p>
                {data.project?.description && <p className="text-sm text-muted-foreground mt-2 max-w-3xl border-l-2 border-primary/40 pl-3" data-testid="report-project-description">{data.project.description}</p>}
              </div>
              {displayKeys.map((k) => <Fragment key={k}>{renderProjectSection(k)}</Fragment>)}
            </div>
          )}
        </div>
      </div>
      <div className="report-print-footer">{title || "Status Report"}</div>
    </Layout>
  );
}

function CustomSectionBlock({ cs, editMode, onUpdate }) {
  return (
    <section className="break-inside-avoid" data-testid={`report-custom-${cs.id}`}>
      {editMode ? (
        <>
          <Input value={cs.title} onChange={(e) => onUpdate(cs.id, { title: e.target.value })} placeholder="Section title" className="font-display font-bold mb-2" data-testid={`custom-title-${cs.id}`} />
          <Textarea rows={5} value={cs.text} onChange={(e) => onUpdate(cs.id, { text: e.target.value })} placeholder="Write anything — Markdown supported (## headings, **bold**, - lists)" data-testid={`custom-text-${cs.id}`} />
        </>
      ) : (
        <>
          <h3 className="font-display text-lg font-bold tracking-tight mb-3 pb-1.5 border-b-2 border-primary/20">{cs.title || "Notes"}</h3>
          <div className="text-sm">
            {cs.text ? <Markdown text={cs.text} /> : <p className="text-sm text-muted-foreground print-hide">Empty section — turn on "Edit report content" in the sidebar to write here.</p>}
          </div>
        </>
      )}
    </section>
  );
}

function PortfolioReport({ pdata, pexec, order, title, period, user, genExec, onGenerate, onToggleAction, onNavigate, onOpenProject, customSections, editMode, onUpdateCustom, execHidden, includeDone = true }) {
  const ov = pdata.overall;
  const allActs = includeDone ? pdata.actions : pdata.actions.filter((a) => a.bucket !== "completed");
  const openActs = allActs.filter((a) => a.bucket !== "completed").sort((a, b) => (a.bucket === "late" ? -1 : 1));
  const doneActs = allActs.filter((a) => a.bucket === "completed");
  const openRisks = [...pdata.risks].sort((a, b) => (b.score || 0) - (a.score || 0));
  const upcomingMs = [...pdata.milestones].sort((a, b) => (a.end_date || "").localeCompare(b.end_date || ""));

  const S = {
    exec: () => (
      <Section title="Portfolio Executive Summary" key="exec">
        {pexec ? (
          <div className="text-sm text-foreground [&_h2]:text-primary [&_h3]:text-primary [&_h4]:text-primary [&_h2]:font-bold [&_h3]:font-bold [&_h4]:font-bold" data-testid="portfolio-exec-summary-body"><ExecFiltered md={pexec} hidden={execHidden} /></div>
        ) : (
          <div className="text-sm text-muted-foreground flex items-center gap-3 print-hide">
            No AI summary yet.
            <Button size="sm" variant="outline" className="gap-2" onClick={onGenerate} disabled={genExec} data-testid="report-gen-portfolio-exec"><Presentation className="h-4 w-4" />{genExec ? "Generating…" : "Generate"}</Button>
          </div>
        )}
      </Section>
    ),
    health: () => (
      <Section title="Portfolio Health Overview" key="health">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Metric label="Projects" value={ov.projects} color="#0044FF" />
          <Metric label="Overall progress" value={`${ov.avg_percent}%`} color="#0044FF" />
          <Metric label="Total tasks" value={ov.tasks} />
          <Metric label="Overdue" value={ov.overdue} color={ov.overdue ? "#EF4444" : undefined} />
        </div>
        <div className="flex flex-wrap gap-4">
          {["blue", "green", "yellow", "red", "gray"].filter((k) => ov.health_mix[k]).map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-sm"><span className="h-3 w-3 rounded-full" style={{ background: HEALTH_COLORS[k] }} /> <b>{ov.health_mix[k]}</b> {HEALTH_LABELS[k]}</span>
          ))}
        </div>
      </Section>
    ),
    projects: () => (
      <Section title="Projects Breakdown" key="projects">
        {pdata.projects.length === 0 ? <Empty text="No projects in scope." /> : (
          <ul className="space-y-2">
            {[...pdata.projects].sort((a, b) => a.avg_percent - b.avg_percent).map((p) => (
              <li key={p.id} onClick={() => onOpenProject?.(p.id)} className="flex items-center gap-3 border-b border-border/50 pb-2 cursor-pointer hover:bg-secondary/40 rounded px-1 -mx-1 transition-colors" data-testid={`portfolio-project-row-${p.id}`}>
                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: HEALTH_COLORS[p.health] }} />
                <span className="text-sm font-medium w-48 truncate">{p.name}</span>
                <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${p.avg_percent}%` }} /></div>
                <span className="text-sm font-semibold w-10 text-right">{p.avg_percent}%</span>
                <span className="text-xs text-muted-foreground w-28 text-right">{p.overdue} overdue · {p.open_risks} risks</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    ),
    phases: () => (
      <Section title="Progress by Phase (rolled up)" key="phases">
        {pdata.phases.length === 0 ? <Empty text="No phase data." /> : (
          <div className="space-y-2">
            {[...pdata.phases].sort((a, b) => b.n - a.n).map((r) => (
              <div key={r.phase} className="flex items-center gap-3">
                <span className="text-sm w-40 truncate">{r.phase}</span>
                <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${r.pct}%` }} /></div>
                <span className="text-sm font-semibold w-10 text-right">{r.pct}%</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    ),
    milestones: () => (
      <Section title="Key Milestones & Dates" key="milestones">
        {upcomingMs.length === 0 ? <Empty text="No milestones defined." /> : (
          <ul className="space-y-1.5">
            {upcomingMs.map((m, i) => (
              <li key={m.id || i} onClick={() => m.project_id && onNavigate?.(`/projects/${m.project_id}?tab=plan&task=${m.id}`)} title="Open in project plan" className="flex items-center justify-between text-sm border-b border-border/50 pb-1.5 cursor-pointer hover:bg-secondary/40 rounded px-1 -mx-1 transition-colors">
                <span>◆ {m.title} <span className="text-muted-foreground">— {m.project}</span></span>
                <span className="text-muted-foreground">{m.end_date || "—"} · {m.status}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    ),
    risks: () => {
      const inProgR = openRisks.filter((r) => r.status !== "closed");
      const closedR = openRisks.filter((r) => r.status === "closed");
      const rrow = (r, i) => {
        const sv = sevOf(r.score || 0);
        const closed = r.status === "closed";
        return (
          <li key={r.id || i} onClick={() => r.project_id && onNavigate?.(`/projects/${r.project_id}?tab=risks`)} title="Open risk register" className="flex items-center gap-2.5 text-sm border-b border-border/50 pb-1.5 cursor-pointer hover:bg-secondary/40 rounded px-1 -mx-1 transition-colors">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: HEALTH_DOT[riskHealth(r)] }} />
            <span className={`truncate flex-1 ${closed ? "line-through text-muted-foreground" : ""}`}>{r.title} <span className="text-muted-foreground">— {r.project} · {r.owner}</span></span>
            {originOf(r) && <span className="text-[11px] text-muted-foreground italic shrink-0">{originOf(r)}</span>}
            <span className="text-[11px] font-bold text-white px-2 py-0.5 rounded shrink-0" style={{ background: closed ? "#9CA3AF" : sv.c }}>{closed ? "Closed" : `${sv.l} (${r.score})`}</span>
          </li>
        );
      };
      return (
        <Section title="Top Portfolio Risks" key="risks">
          {openRisks.length === 0 ? <Empty text="No risks logged." /> : (
            <>
              <DotLegend />
              {inProgR.length > 0 && (
                <ActionGroup id="portfolio-risks-in-progress" testid="report-risks-label-portfolio-in-progress" label="In Progress" color="#10B981" count={inProgR.length} defaultOpen={false}>
                  {SEVERITY_BANDS.map(([lb, col, test]) => {
                    const g = inProgR.filter((r) => test(r.score));
                    return g.length === 0 ? null : (
                      <div className="ml-3" key={lb}>
                        <ActionGroup id={`portfolio-risks-${lb}`} testid={`report-portfolio-risks-sev-${lb.toLowerCase()}`} label={lb} color={col} count={g.length}>
                          <ul className="space-y-1.5">{g.map(rrow)}</ul>
                        </ActionGroup>
                      </div>
                    );
                  })}
                </ActionGroup>
              )}
              {closedR.length > 0 && (
                <ActionGroup id="portfolio-risks-closed" testid="report-risks-label-portfolio-closed" label="Closed" color="#2563EB" count={closedR.length} defaultOpen={false}>
                  <ul className="space-y-1.5">{closedR.map(rrow)}</ul>
                </ActionGroup>
              )}
            </>
          )}
        </Section>
      );
    },
    actions: () => {
      const row = (a, i) => (
        <li key={a.id || i} className="flex items-center gap-2.5 text-sm border-b border-border/50 pb-1.5" data-testid={`report-portfolio-action-${a.id || i}`}>
          {a.id && <input type="checkbox" className="accent-primary h-4 w-4 shrink-0 cursor-pointer print-hide" checked={a.bucket === "completed"} onChange={(e) => onToggleAction?.(a, e.target.checked)} title={a.bucket === "completed" ? "Reopen" : "Mark complete"} data-testid={`report-portfolio-action-check-${a.id}`} />}
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: HEALTH_DOT[actionHealth(a)] }} />
          {a.display_id && <span className="font-mono text-[10px] font-bold text-primary shrink-0">{a.display_id}</span>}
          <span onClick={() => a.project_id && onNavigate?.(`/projects/${a.project_id}?tab=actions`)} title="Open action items register" className={`truncate flex-1 cursor-pointer hover:underline ${a.bucket === "completed" ? "line-through text-muted-foreground" : ""}`}>{a.title} <span className="text-muted-foreground">— {a.project} · {a.owner}</span></span>
          {originOf(a) && <span className="text-[11px] text-muted-foreground italic shrink-0">{originOf(a)}</span>}
          <span className="text-muted-foreground shrink-0">{a.due_date || "—"}{a.bucket === "late" ? " · late" : ""}</span>
        </li>
      );
      return (
        <Section title="Action Items" key="actions">
          {allActs.length === 0 ? <Empty text={includeDone ? "No action items." : "No open action items."} /> : (
            <>
              <div className="flex gap-4 mb-2 text-sm">
                {["late", "pending", "completed"].filter((b) => includeDone || b !== "completed").map((b) => (
                  <span key={b} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: b === "late" ? "#EF4444" : b === "pending" ? "#F59E0B" : "#2563EB" }} /> <b>{allActs.filter((a) => a.bucket === b).length}</b> {b}</span>
                ))}
              </div>
              <DotLegend />
              {openActs.length > 0 && (
                <ActionGroup id="portfolio-in-progress" testid="report-actions-label-portfolio-in-progress" label="In Progress" color="#10B981" count={openActs.length} defaultOpen={false}>
                  <ul className="space-y-1.5">{openActs.map(row)}</ul>
                </ActionGroup>
              )}
              {doneActs.length > 0 && (
                <ActionGroup id="portfolio-closed" testid="report-actions-label-portfolio-closed" label="Closed" color="#2563EB" count={doneActs.length} defaultOpen={false}>
                  <ul className="space-y-1.5">{doneActs.map(row)}</ul>
                </ActionGroup>
              )}
            </>
          )}
        </Section>
      );
    },
    blockers: () => (
      <Section title="Blockers" key="blockers">
        {pdata.blockers.length === 0 ? <Empty text="No blockers 🎉" /> : (
          <ul className="space-y-1.5">
            {pdata.blockers.map((t, i) => (
              <li key={t.id || i} onClick={() => t.project_id && onNavigate?.(`/projects/${t.project_id}?tab=plan&task=${t.id}`)} title="Open in plan" className="flex items-center justify-between text-sm border-b border-border/50 pb-1.5 cursor-pointer hover:bg-secondary/40 rounded px-1 -mx-1 transition-colors">
                <span>{t.title} <span className="text-muted-foreground">— {t.project}</span></span><span className="text-muted-foreground">{t.assignee} · {t.phase}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    ),
    budget: () => (
      pdata.budget ? (
        <Section title="Portfolio Budget Snapshot" key="budget">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Total budget" value={pdata.budget.budget != null ? money(pdata.budget.budget, pdata.budget.currency) : "—"} />
            <Metric label="Planned cost" value={money(pdata.budget.planned_cost, pdata.budget.currency)} />
            <Metric label="Variance" value={pdata.budget.budget_variance == null ? "—" : money(pdata.budget.budget_variance, pdata.budget.currency)} color={pdata.budget.budget_variance < 0 ? "#EF4444" : "#10B981"} />
            <Metric label="Used" value={pdata.budget.percent_used == null ? "—" : `${pdata.budget.percent_used}%`} />
          </div>
        </Section>
      ) : null
    ),
  };

  return (
    <div className="space-y-8">
      <div className="border-b border-border pb-4">
        <h2 className="font-display text-3xl font-extrabold tracking-tight">{title}</h2>
        <p className="text-sm text-muted-foreground mt-1">{ov.projects} projects · Reporting date {period} · Prepared by {user?.name}</p>
      </div>
      {order.map((k) => {
        if (k.startsWith("custom:")) {
          const cs = customSections.find((c) => `custom:${c.id}` === k);
          return cs ? <CustomSectionBlock key={k} cs={cs} editMode={editMode} onUpdate={onUpdateCustom} /> : null;
        }
        return S[k] ? <Fragment key={k}>{S[k]()}</Fragment> : null;
      })}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="break-inside-avoid">
      <h3 className="font-display text-lg font-bold tracking-tight mb-3 pb-1.5 border-b-2 border-primary/20">{title}</h3>
      {children}
    </section>
  );
}
function Metric({ label, value, color, onClick }) {
  const cls = "border border-border rounded-md p-3" + (onClick ? " text-left w-full hover:border-foreground/40 hover:shadow-sm transition-all cursor-pointer" : "");
  const inner = (
    <>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-display text-xl font-extrabold mt-1" style={color ? { color } : {}}>{value}</p>
    </>
  );
  return onClick ? <button type="button" className={cls} onClick={onClick} data-testid={`stat-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}>{inner}</button> : <div className={cls}>{inner}</div>;
}
function Empty({ text }) { return <p className="text-sm text-muted-foreground">{text}</p>; }
